import { useEffect, useState } from "react";
import { Copy, RefreshCw, Shield, Crown, Zap, Server, Cloud, GitBranch, Clock, Infinity as InfinityIcon, AlertCircle, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso); }
}

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function envChip(env) {
  if (env === "production") return { label: "PRODUCTION", cls: "bg-rose-500/15 text-rose-700 border-rose-500/30" };
  if (env === "preview")    return { label: "PREVIEW",    cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" };
  return { label: "UNKNOWN", cls: "bg-paper-100 text-ink-700 border-paper-200" };
}

function detectClientEnv() {
  if (typeof window === "undefined") return "unknown";
  const host = window.location.hostname;
  if (host.includes(".emergent.host")) return "production";
  if (host.includes("preview.emergentagent.com")) return "preview";
  if (host === "localhost" || host.startsWith("127.")) return "preview";
  return "unknown";
}

function Row({ label, value, valueClassName = "", copyable = false }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1200);
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-paper-100 last:border-b-0">
      <span className="text-[10px] uppercase tracking-[0.16em] text-ink-500 pt-1 shrink-0">{label}</span>
      <span className={`text-right text-sm text-ink-900 break-all ${valueClassName} inline-flex items-center gap-2 justify-end`}>
        <span>{value ?? "—"}</span>
        {copyable && value != null && (
          <button type="button" onClick={doCopy} className="w-7 h-7 rounded-full hover:bg-paper-100 inline-flex items-center justify-center text-ink-400 hover:text-ink-700 shrink-0" aria-label={`Copy ${label}`}>
            {copied ? <Check className="w-3.5 h-3.5 text-sage-700" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * AdminDebugPanel — Settings → Admin debug section.
 * Shows role, subscription, AI quota, build version, deployment timestamp, environment.
 * Visible to admin users only.
 */
export default function AdminDebugPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const clientEnv = detectClientEnv();

  const fetchDebug = async () => {
    try {
      const r = await api.get("/system/debug", {
        headers: { "X-Client-Env": detectClientEnv() },
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status !== 401 && e?.response?.status !== 403) {
        toast.error("Could not load debug info.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(async () => { if (!cancelled) await fetchDebug(); });
    return () => { cancelled = true; };
  }, []);

  const onRefresh = () => {
    setRefreshing(true);
    fetchDebug();
    window.dispatchEvent(new Event("billing:refresh"));
  };

  if (loading) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-6" data-testid="admin-debug-loading">
        <div className="text-sm text-ink-500">Loading account &amp; environment info…</div>
      </div>
    );
  }
  if (!data) return null;

  const user = data.user || {};
  const sub = data.subscription || {};
  const quota = data.ai_quota || {};
  const build = data.build || {};

  const serverEnv = build.environment || "unknown";
  const envBadge = envChip(serverEnv);
  const envMismatch = clientEnv !== "unknown" && serverEnv !== "unknown" && clientEnv !== serverEnv;

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 space-y-6" data-testid="admin-debug-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline text-violet-700 mb-1.5 inline-flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" /> Admin · Debug panel
          </div>
          <h2 className="font-heading text-2xl text-ink-900">Account &amp; deployment</h2>
          <p className="text-sm text-ink-600 mt-1">Confirm role, subscription, quota, build version &amp; environment at a glance.</p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          data-testid="admin-debug-refresh-btn"
          className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm text-ink-700 inline-flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {envMismatch && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-3 text-sm text-amber-900 inline-flex items-start gap-2" data-testid="env-mismatch-warning">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Browser is on <span className="font-medium">{clientEnv}</span> but the API reports <span className="font-medium">{serverEnv}</span>. This usually means the backend deployment hasn&rsquo;t caught up — try a hard refresh.</span>
        </div>
      )}

      {/* ACCOUNT card */}
      <section data-testid="debug-account-card">
        <div className="label-overline mb-2 inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Account</div>
        <div className="bg-paper-50 border border-paper-200 rounded-2xl px-4">
          <Row label="Email" value={user.email} copyable />
          <Row label="User ID" value={user.id} copyable />
          <Row
            label="Role"
            value={user.is_admin ? "Admin" : (user.role || "Therapist")}
            valueClassName={user.is_admin ? "text-violet-700 font-medium" : ""}
          />
        </div>
      </section>

      {/* SUBSCRIPTION + QUOTA card */}
      <section data-testid="debug-subscription-card">
        <div className="label-overline mb-2 inline-flex items-center gap-1.5">
          {sub.tier === "free" ? <Zap className="w-3.5 h-3.5" /> : <Crown className="w-3.5 h-3.5" />} Subscription &amp; AI quota
        </div>
        <div className="bg-paper-50 border border-paper-200 rounded-2xl px-4">
          <Row
            label="Subscription tier"
            value={(sub.tier || "free").charAt(0).toUpperCase() + (sub.tier || "free").slice(1)}
            valueClassName={sub.tier && sub.tier !== "free" ? "text-sage-700 font-medium" : ""}
          />
          <Row
            label="Quota exempt"
            value={sub.exempt ? `Yes${sub.exempt_reason ? ` (${sub.exempt_reason})` : ""}` : "No"}
            valueClassName={sub.exempt ? "text-violet-700 font-medium" : ""}
          />
          <Row
            label="AI workflows used (30d)"
            value={quota.used_last_30_days != null ? String(quota.used_last_30_days) : "—"}
          />
          <Row
            label="AI workflow limit"
            value={quota.unlimited ? "Unlimited" : (quota.limit != null ? `${quota.limit} / month` : "—")}
            valueClassName={quota.unlimited ? "text-sage-700 font-medium inline-flex items-center gap-1" : ""}
          />
          <Row
            label="Remaining this month"
            value={quota.unlimited ? "∞" : (quota.remaining != null ? String(quota.remaining) : "—")}
          />
          {user.early_adopter?.granted && (
            <Row
              label="Early Adopter"
              value={`Lifetime ${user.early_adopter.tier || "pro"}${user.early_adopter.granted_at ? ` · since ${fmtDate(user.early_adopter.granted_at)}` : ""}`}
              valueClassName="text-violet-700"
            />
          )}
        </div>
      </section>

      {/* DEPLOYMENT card */}
      <section data-testid="debug-deployment-card">
        <div className="label-overline mb-2 inline-flex items-center gap-1.5"><Server className="w-3.5 h-3.5" /> Build &amp; deployment</div>
        <div className="bg-paper-50 border border-paper-200 rounded-2xl px-4">
          <Row
            label="Environment"
            value={
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${envBadge.cls}`} data-testid="debug-env-badge">
                <Cloud className="w-3 h-3" /> {envBadge.label}
              </span>
            }
          />
          <Row label="API host" value={(() => {
            try { return new URL(build.public_url || (typeof window !== "undefined" ? window.location.origin : "")).hostname; }
            catch { return build.public_url || "—"; }
          })()} copyable />
          <Row label="Browser host" value={typeof window !== "undefined" ? window.location.host : "—"} copyable />
          <Row label="Build version" value={build.version || "—"} valueClassName="font-mono text-xs" copyable />
          <Row
            label="Deployed at"
            value={build.deployed_at ? `${fmtDate(build.deployed_at)} · ${relTime(build.deployed_at)}` : "—"}
          />
          <Row label="Server time" value={fmtDate(data.server_now)} />
          {build.owner_emails?.length > 0 && (
            <Row label="Owner emails" value={build.owner_emails.join(", ")} valueClassName="font-mono text-xs" />
          )}
        </div>
        <p className="text-[11px] text-ink-500 mt-2 leading-relaxed">
          Build version comes from the <code className="font-mono text-[10px] bg-paper-100 px-1 rounded">APP_VERSION</code> env var on the backend.
          Set it at deploy-time (e.g. to your git short SHA) so this row updates automatically.
        </p>
      </section>
    </div>
  );
}
